import { useEffect } from 'react';
import {
  chargeSessionsMessageSchema,
  sessionDetailMessageSchema,
  sessionDetailRequestMessageSchema,
  viewportMessageSchema,
  type ChargeSessionsMessage,
  type SessionCluster,
  type SessionDetailMessage,
  type SessionLeaf,
  type SessionLevel,
  type Viewport,
} from '../types/charge-session.types';

/**
 * DEV-ONLY mock parent for the charge-session map (BIZOPS-1588, contract v2 —
 * server-driven viewport clustering).
 *
 * When the SPA runs standalone (no embedding Hub, no backend) this module
 * impersonates the Hub studio-viewer parent + the gateway's H3 clustering so the
 * whole viewport protocol can be exercised locally:
 *
 *   1. Listens for the SPA's outbound `monta:viewport` and replies with a
 *      `monta:charge-sessions` whose contents depend on the requested zoom:
 *      below the leaf threshold it returns H3-ish *clusters* (the mock leaves
 *      binned into a grid that gets finer as you zoom, so a few big bubbles at
 *      low zoom split into many small ones); at/above the leaf threshold
 *      (z >= {@link LEAF_ZOOM_THRESHOLD}) it returns the individual *leaves* in
 *      view. Clusters/leaves are restricted to the requested bbox.
 *   2. Re-pushes the *current* viewport's clusters/leaves every
 *      {@link MOCK_TICK_MS} (jittering counts) so the live-refresh + marker pulse
 *      are visibly working.
 *   3. Listens for the SPA's outbound `monta:session-detail-request` and replies
 *      with a matching `monta:session-detail` for the requested leaf.
 *
 * The dataset is ~{@link MOCK_LEAF_COUNT} active-session points spread across
 * Denmark (weighted toward real population centres plus rural scatter) so the
 * viewport clustering is properly demoable at country/region/city/leaf zoom. It
 * is generated once, deterministically (seeded PRNG — see {@link buildMockLeaves}),
 * so the point SET is stable across reloads and test runs; only the per-tick
 * `activeSessionCount` jitter changes for "liveness".
 *
 * Every payload is `.parse()`d through the real contract schemas before posting,
 * so the mock can never drift from the wire format the real parent must honor.
 * Messages are posted with `targetOrigin = window.location.origin`; the SPA only
 * accepts them because `env.ts` adds `window.location.origin` to the allowlist
 * under the SAME DEV + `mock=1` gate (see `isMockChargeSessionsActive`).
 *
 * HARD GATE: nothing here runs unless `import.meta.env.DEV` AND the URL carries
 * `mock=1`. It is tree-shaken out of production builds.
 */

/** Live-refresh cadence (ms) for re-pushing the current viewport. */
const MOCK_TICK_MS = 10_000;

/**
 * Leaf zoom threshold — at/above this the gateway (and this mock) returns
 * individual leaf points instead of clusters. Mirrors the gateway's
 * zoom→H3-res ladder (`leaves >= z16`, spec §15).
 */
export const LEAF_ZOOM_THRESHOLD = 16;

/** Target size of the deterministic Denmark-wide mock dataset. */
export const MOCK_LEAF_COUNT = 1000;

/**
 * Denmark geographic bounds the dataset is clamped to. Roughly the mainland +
 * islands + Bornholm envelope (lat 54.5–57.8, lng 8.0–15.2).
 */
export const DK_BOUNDS = {
  minLat: 54.5,
  maxLat: 57.8,
  minLng: 8.0,
  maxLng: 15.2,
} as const;

/**
 * Is the DEV charge-session mock parent active?
 *
 * The complete gate: true ONLY in a dev build (`import.meta.env.DEV`) when the
 * URL carries `mock=1` AND `mapType=chargeSessions`. Production builds always
 * return false (the `import.meta.env.DEV` branch is dead-code-eliminated).
 *
 * NOTE: `env.ts` inlines the identical check to decide whether to add
 * `window.location.origin` to the postMessage allowlist. The two MUST stay in
 * sync; they are duplicated (rather than shared) so the central `env.ts` module
 * never imports a feature module.
 */
export function isMockChargeSessionsActive(): boolean {
  if (!import.meta.env.DEV) {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  return (
    params.get('mock') === '1' && params.get('mapType') === 'chargeSessions'
  );
}

/** Base shape for a mock leaf, before per-tick count jitter. */
interface MockLeafSeed {
  id: string;
  level: SessionLevel;
  name: string;
  lat: number;
  lng: number;
  baseActiveSessionCount: number;
}

/**
 * A weighted population centre the dataset clusters points around. `weight`
 * drives the share of points assigned to the centre; `spreadDeg` is the Gaussian
 * scatter radius (in degrees of latitude) around it. Real Danish coordinates so
 * the demo reads as a plausible national footprint.
 */
interface PopulationCentre {
  name: string;
  lat: number;
  lng: number;
  weight: number;
  spreadDeg: number;
}

/**
 * Weighted centres: the big metros carry the most points, with the
 * Trekantområde (Kolding/Vejle/Fredericia), the larger Jutland/Funen/Zealand
 * towns, and Bornholm filling out the map. Weights are relative, not absolute
 * counts.
 */
const POPULATION_CENTRES: ReadonlyArray<PopulationCentre> = [
  // --- Greater Copenhagen / Sjælland ---
  {
    name: 'Copenhagen',
    lat: 55.6761,
    lng: 12.5683,
    weight: 230,
    spreadDeg: 0.18,
  },
  { name: 'Roskilde', lat: 55.6415, lng: 12.0803, weight: 35, spreadDeg: 0.06 },
  { name: 'Helsingør', lat: 56.036, lng: 12.5899, weight: 22, spreadDeg: 0.05 },
  { name: 'Hillerød', lat: 55.9279, lng: 12.301, weight: 18, spreadDeg: 0.05 },
  { name: 'Køge', lat: 55.458, lng: 12.182, weight: 16, spreadDeg: 0.05 },
  { name: 'Næstved', lat: 55.23, lng: 11.76, weight: 16, spreadDeg: 0.06 },
  { name: 'Slagelse', lat: 55.403, lng: 11.354, weight: 14, spreadDeg: 0.06 },
  // --- East Jutland ---
  { name: 'Aarhus', lat: 56.1629, lng: 10.2039, weight: 110, spreadDeg: 0.14 },
  { name: 'Randers', lat: 56.4607, lng: 10.0369, weight: 26, spreadDeg: 0.06 },
  { name: 'Horsens', lat: 55.8607, lng: 9.8503, weight: 24, spreadDeg: 0.06 },
  { name: 'Silkeborg', lat: 56.1697, lng: 9.545, weight: 20, spreadDeg: 0.06 },
  { name: 'Viborg', lat: 56.453, lng: 9.402, weight: 16, spreadDeg: 0.06 },
  // --- Funen ---
  { name: 'Odense', lat: 55.4038, lng: 10.4024, weight: 80, spreadDeg: 0.12 },
  { name: 'Svendborg', lat: 55.059, lng: 10.607, weight: 14, spreadDeg: 0.05 },
  // --- North Jutland ---
  { name: 'Aalborg', lat: 57.0488, lng: 9.9217, weight: 70, spreadDeg: 0.12 },
  {
    name: 'Frederikshavn',
    lat: 57.4407,
    lng: 10.534,
    weight: 14,
    spreadDeg: 0.05,
  },
  { name: 'Hjørring', lat: 57.4642, lng: 9.9819, weight: 12, spreadDeg: 0.05 },
  // --- West / South Jutland + Trekantområde ---
  { name: 'Esbjerg', lat: 55.4765, lng: 8.4594, weight: 50, spreadDeg: 0.1 },
  { name: 'Kolding', lat: 55.4904, lng: 9.4722, weight: 30, spreadDeg: 0.06 },
  { name: 'Vejle', lat: 55.709, lng: 9.535, weight: 28, spreadDeg: 0.06 },
  {
    name: 'Fredericia',
    lat: 55.5657,
    lng: 9.7527,
    weight: 22,
    spreadDeg: 0.05,
  },
  { name: 'Herning', lat: 56.139, lng: 8.974, weight: 22, spreadDeg: 0.07 },
  { name: 'Sønderborg', lat: 54.909, lng: 9.792, weight: 16, spreadDeg: 0.06 },
  { name: 'Aabenraa', lat: 55.044, lng: 9.418, weight: 10, spreadDeg: 0.05 },
  // --- Bornholm ---
  { name: 'Rønne', lat: 55.101, lng: 14.7065, weight: 12, spreadDeg: 0.05 },
];

/** Fixed seed → the dataset is identical on every build / reload / test run. */
const DATASET_SEED = 0x1588c0de;

/** Fraction of the dataset placed as uniform rural scatter (vs. around centres). */
const RURAL_FRACTION = 0.18;

/**
 * mulberry32 — a tiny, fast, fully deterministic 32-bit PRNG. Seeded once, it
 * yields the same stream every run, which is what makes the dataset stable
 * across reloads and test runs (no `Math.random`).
 */
function createSeededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return function nextRandom(): number {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Clamp a value into the inclusive [lo, hi] range. */
function clamp(value: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, value));
}

/**
 * One standard-normal sample via Box–Muller, drawing two uniforms from the
 * shared seeded stream so the scatter stays deterministic.
 */
function nextGaussian(nextRandom: () => number): number {
  const u1 = Math.max(1e-9, nextRandom());
  const u2 = nextRandom();
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}

/**
 * Pick a population centre weighted by `weight` from a uniform draw. `roll` is a
 * value in [0, totalWeight). Walks every centre but the last, subtracting its
 * weight; the final centre is the natural fall-through for the remaining
 * (highest) weight band, which also covers any floating-point overshoot of
 * `totalWeight` — so there is no separate unreachable fallback.
 */
function pickCentre(roll: number): PopulationCentre {
  let remaining = roll;
  for (let index = 0; index < POPULATION_CENTRES.length - 1; index += 1) {
    const centre = POPULATION_CENTRES[index]!;
    remaining -= centre.weight;
    if (remaining <= 0) {
      return centre;
    }
  }
  return POPULATION_CENTRES[POPULATION_CENTRES.length - 1]!;
}

/**
 * Derive a small, plausible active-session count for a point from a uniform
 * draw: mostly 1, some 2–5, and a few busy "sites" in the 8–30 range. Returned
 * value is the per-point baseline that {@link jitterCount} wiggles per tick.
 */
function baseCountFromRoll(roll: number): number {
  if (roll < 0.62) {
    return 1;
  }
  if (roll < 0.93) {
    return 2 + (Math.floor(roll * 40) % 4); // 2–5
  }
  // A handful of larger sites (depots / hubs).
  return 8 + (Math.floor(roll * 100) % 23); // 8–30
}

/**
 * Build the deterministic Denmark-wide leaf dataset (~{@link MOCK_LEAF_COUNT}
 * points). Most points scatter (Gaussian) around weighted population centres;
 * the rest are uniform rural scatter. Everything is clamped into
 * {@link DK_BOUNDS}. Levels alternate location/evse so both detail-panel shapes
 * are reachable, and each point carries a stable id + name.
 *
 * Pure + seeded: same input → same dataset, every run.
 */
export function buildMockLeaves(): MockLeafSeed[] {
  const nextRandom = createSeededRandom(DATASET_SEED);
  const totalWeight = POPULATION_CENTRES.reduce(
    (sum, centre) => sum + centre.weight,
    0
  );
  const ruralCount = Math.round(MOCK_LEAF_COUNT * RURAL_FRACTION);
  const centreCount = MOCK_LEAF_COUNT - ruralCount;

  const leaves: MockLeafSeed[] = [];

  for (let index = 0; index < centreCount; index += 1) {
    const centre = pickCentre(nextRandom() * totalWeight);
    const lat = clamp(
      centre.lat + nextGaussian(nextRandom) * centre.spreadDeg,
      DK_BOUNDS.minLat,
      DK_BOUNDS.maxLat
    );
    // Longitude degrees are "narrower" at these latitudes, so widen the spread a
    // little so towns don't look vertically stretched.
    const lng = clamp(
      centre.lng + nextGaussian(nextRandom) * centre.spreadDeg * 1.6,
      DK_BOUNDS.minLng,
      DK_BOUNDS.maxLng
    );
    const level: SessionLevel = index % 3 === 0 ? 'location' : 'evse';
    const baseActiveSessionCount = baseCountFromRoll(nextRandom());
    leaves.push({
      id: String(100000 + index),
      level: level,
      name: `${centre.name} ${level === 'location' ? 'Site' : 'EVSE'} ${index + 1}`,
      lat: lat,
      lng: lng,
      baseActiveSessionCount: baseActiveSessionCount,
    });
  }

  for (let index = 0; index < ruralCount; index += 1) {
    const lat = clamp(
      DK_BOUNDS.minLat + nextRandom() * (DK_BOUNDS.maxLat - DK_BOUNDS.minLat),
      DK_BOUNDS.minLat,
      DK_BOUNDS.maxLat
    );
    const lng = clamp(
      DK_BOUNDS.minLng + nextRandom() * (DK_BOUNDS.maxLng - DK_BOUNDS.minLng),
      DK_BOUNDS.minLng,
      DK_BOUNDS.maxLng
    );
    const globalIndex = centreCount + index;
    const level: SessionLevel = index % 4 === 0 ? 'location' : 'evse';
    leaves.push({
      id: String(100000 + globalIndex),
      level: level,
      name: `Rural ${level === 'location' ? 'Site' : 'EVSE'} ${globalIndex + 1}`,
      lat: lat,
      lng: lng,
      baseActiveSessionCount: baseCountFromRoll(nextRandom()),
    });
  }

  return leaves;
}

/**
 * The frozen mock dataset, built once at module load. Stable across reloads and
 * test runs (deterministic seed); only per-tick `activeSessionCount` jitter
 * changes thereafter.
 */
const MOCK_LEAVES: ReadonlyArray<MockLeafSeed> = buildMockLeaves();

/** Fast id → seed lookup for detail-on-click of any leaf. */
const MOCK_LEAVES_BY_ID: ReadonlyMap<string, MockLeafSeed> = new Map(
  MOCK_LEAVES.map((leaf) => [leaf.id, leaf])
);

/** Deterministic per-leaf jitter for a given tick: base ± up to 2, floored at 0. */
function jitterCount(base: number, index: number, tick: number): number {
  // Cheap reproducible pseudo-noise — no Math.random so ticks are stable/testable.
  const wave = Math.sin(tick * 1.3 + index * 2.1);
  const delta = Math.round(wave * 2);
  return Math.max(0, base + delta);
}

/** The jittered leaves for a tick, as concrete (parsed-ready) leaf objects. */
function leavesForTick(tick: number): SessionLeaf[] {
  return MOCK_LEAVES.map((seed, index) => ({
    id: seed.id,
    level: seed.level,
    name: seed.name,
    lat: seed.lat,
    lng: seed.lng,
    activeSessionCount: jitterCount(seed.baseActiveSessionCount, index, tick),
  }));
}

/** Is a coordinate inside the requested viewport bbox? */
function isInViewport(lat: number, lng: number, viewport: Viewport): boolean {
  return (
    lat <= viewport.top &&
    lat >= viewport.bottom &&
    lng >= viewport.left &&
    lng <= viewport.right
  );
}

/**
 * Grid cell size (in degrees) for binning leaves into clusters at a given zoom.
 * Coarser (bigger cells → fewer, bigger clusters) at low zoom; finer as you
 * zoom in, so clusters visibly split. Tuned for the ~1000-point Denmark dataset:
 * a country-wide view (z<=7) yields a handful of big bubbles, region/metro zoom
 * yields more, and city/district zoom yields many fine ones before leaves take
 * over at z>=16. Mirrors a zoom→resolution ladder.
 */
function gridSizeForZoom(zoom: number): number {
  if (zoom <= 7) {
    return 2.0;
  }
  if (zoom <= 9) {
    return 0.8;
  }
  if (zoom <= 11) {
    return 0.2;
  }
  if (zoom <= 13) {
    return 0.05;
  }
  return 0.02;
}

/**
 * A coarse stand-in for an H3 resolution at a given zoom (clusters report the
 * resolution they were binned at). Monotonic in zoom; values are illustrative.
 * Rungs line up with {@link gridSizeForZoom}.
 */
function resolutionForZoom(zoom: number): number {
  if (zoom <= 7) {
    return 3;
  }
  if (zoom <= 9) {
    return 4;
  }
  if (zoom <= 11) {
    return 6;
  }
  if (zoom <= 13) {
    return 8;
  }
  return 9;
}

/** Bin in-view leaves into grid clusters for the given zoom. */
function clusterLeaves(leaves: SessionLeaf[], zoom: number): SessionCluster[] {
  const gridSize = gridSizeForZoom(zoom);
  const cells = new Map<
    string,
    { latSum: number; lngSum: number; pointCount: number; sessionSum: number }
  >();

  for (const leaf of leaves) {
    const latCell = Math.floor(leaf.lat / gridSize);
    const lngCell = Math.floor(leaf.lng / gridSize);
    const key = `${latCell}:${lngCell}`;
    const cell = cells.get(key) ?? {
      latSum: 0,
      lngSum: 0,
      pointCount: 0,
      sessionSum: 0,
    };
    cell.latSum += leaf.lat;
    cell.lngSum += leaf.lng;
    cell.pointCount += 1;
    cell.sessionSum += leaf.activeSessionCount;
    cells.set(key, cell);
  }

  const clusters: SessionCluster[] = [];
  for (const [key, cell] of cells) {
    clusters.push({
      // A fake-but-stable "H3-like" hex id for the cell so re-pushes are stable.
      id: `mock-h3-${key}`,
      lat: cell.latSum / cell.pointCount,
      lng: cell.lngSum / cell.pointCount,
      pointCount: cell.pointCount,
      activeSessionCount: cell.sessionSum,
    });
  }
  return clusters;
}

/**
 * Build a Tier-1 `monta:charge-sessions` payload for a requested viewport + tick.
 * Below the leaf threshold it returns clusters (binned, finer as you zoom);
 * at/above it returns the in-view leaves. Parsed through the real schema.
 */
export function buildClustersMessage(
  viewport: Viewport,
  tick: number
): ChargeSessionsMessage {
  const inView = leavesForTick(tick).filter((leaf) =>
    isInViewport(leaf.lat, leaf.lng, viewport)
  );

  const isLeafZoom = viewport.zoom >= LEAF_ZOOM_THRESHOLD;
  const clusters = isLeafZoom ? [] : clusterLeaves(inView, viewport.zoom);
  const leaves = isLeafZoom ? inView : [];
  // Mirror the real gateway (1591): at leaf zoom it emits individual leaves and
  // reports `resolution: null` (there is no cluster resolution); only at cluster
  // zooms does it carry a numeric H3 resolution. The pre-fix mock always sent a
  // number here, which masked the SPA's non-null `resolution` schema bug.
  const resolution = isLeafZoom ? null : resolutionForZoom(viewport.zoom);

  return chargeSessionsMessageSchema.parse({
    type: 'monta:charge-sessions',
    generatedAt: new Date().toISOString(),
    zoom: viewport.zoom,
    resolution: resolution,
    clusters: clusters,
    leaves: leaves,
  });
}

/**
 * One row of canned session data. The real charge-state vocabulary is used so
 * the detail panel exercises its `chargeSessions.state.*` label mapping; some
 * rows carry a `consumerLabel` (PII) to exercise the authenticated view.
 */
interface MockSessionSeed {
  evseSuffix: string;
  chargePointName: string;
  state: string;
  kwh: number;
  startedMinutesAgo: number;
  costLabel?: string;
  currency?: string;
  consumerLabel?: string;
}

const MOCK_SESSION_SEEDS: ReadonlyArray<MockSessionSeed> = [
  {
    evseSuffix: 'EVSE-1',
    chargePointName: 'CP North A1',
    state: 'charging',
    kwh: 18.4,
    startedMinutesAgo: 42,
    costLabel: '63.20',
    currency: 'DKK',
    consumerLabel: 'Anders Jensen',
  },
  {
    evseSuffix: 'EVSE-2',
    chargePointName: 'CP North A2',
    state: 'paused',
    kwh: 7.1,
    startedMinutesAgo: 15,
    costLabel: '24.05',
    currency: 'DKK',
  },
  {
    evseSuffix: 'EVSE-3',
    chargePointName: 'CP North B1',
    state: 'suspended_ev',
    kwh: 31.9,
    startedMinutesAgo: 88,
    costLabel: '109.50',
    currency: 'DKK',
    consumerLabel: 'Mette Sørensen',
  },
  {
    evseSuffix: 'EVSE-4',
    chargePointName: 'CP North B2',
    state: 'starting',
    kwh: 0.3,
    startedMinutesAgo: 1,
  },
  {
    evseSuffix: 'EVSE-5',
    chargePointName: 'CP North C1',
    state: 'charging',
    kwh: 12.7,
    startedMinutesAgo: 27,
    costLabel: '44.10',
    currency: 'DKK',
    consumerLabel: 'Lars Nielsen',
  },
];

/**
 * Derive a deterministic, stable session-row count for an arbitrary leaf so
 * detail-on-click works for ANY leaf id (not just the few canned ones). EVSE
 * leaves resolve to a single session; locations fan out to several, sized by a
 * cheap hash of the id so different sites show different-length lists.
 */
function sessionCountForLeaf(id: string, level: SessionLevel): number {
  if (level === 'evse') {
    return 1;
  }
  let hash = 0;
  for (let index = 0; index < id.length; index += 1) {
    hash = (hash * 31 + id.charCodeAt(index)) | 0;
  }
  const span = MOCK_SESSION_SEEDS.length - 1; // 1..seeds.length
  return 2 + (Math.abs(hash) % span);
}

/**
 * Build a Tier-2 `monta:session-detail` payload for a requested leaf. Works for
 * ANY leaf id: known ids reuse their seed name/coords, unknown ids fall back to
 * a synthetic name and Denmark-centroid coords. The number of session rows
 * tracks the leaf level (+ a stable per-id hash for locations). Parsed through
 * the real schema before return.
 */
export function buildSessionDetailMessage(
  id: string,
  level: SessionLevel
): SessionDetailMessage {
  const leaf = MOCK_LEAVES_BY_ID.get(id);
  const name = leaf?.name ?? `Leaf ${id}`;
  const lat = leaf?.lat ?? 56.0;
  const lng = leaf?.lng ?? 11.6;

  const seedCount = sessionCountForLeaf(id, level);
  const now = Date.now();

  const sessions = MOCK_SESSION_SEEDS.slice(0, seedCount).map(
    (seed, index) => ({
      // Stable, leaf-scoped session id so re-clicks are idempotent.
      sessionId: `${id}-${index + 1}`,
      evseName: `${name} · ${seed.evseSuffix}`,
      chargePointName: seed.chargePointName,
      state: seed.state,
      kwh: seed.kwh,
      startedAt: new Date(now - seed.startedMinutesAgo * 60_000).toISOString(),
      consumerLabel: seed.consumerLabel ?? null,
      costLabel: seed.costLabel ?? null,
      currency: seed.currency ?? null,
    })
  );

  return sessionDetailMessageSchema.parse({
    type: 'monta:session-detail',
    id: id,
    level: level,
    name: name,
    lat: lat,
    lng: lng,
    sessions: sessions,
  });
}

/** Post a contract message to our own window as the mock parent would. */
function postAsMockParent(
  message: ChargeSessionsMessage | SessionDetailMessage
): void {
  window.postMessage(message, window.location.origin);
}

/**
 * Start the DEV mock parent: answer the SPA's `monta:viewport` with
 * clusters/leaves for that view, re-push the current view on an interval (live
 * refresh), and answer detail requests. Returns a cleanup function that stops
 * the interval and removes the listener. Caller is responsible for the DEV +
 * `mock=1` + `mapType=chargeSessions` gate.
 */
export function startChargeSessionMock(): () => void {
  let tick = 0;
  // The most recent viewport the SPA reported; the live-refresh re-pushes it.
  let currentViewport: Viewport | null = null;

  const pushCurrentViewport = () => {
    if (!currentViewport) {
      return;
    }
    postAsMockParent(buildClustersMessage(currentViewport, tick));
    tick += 1;
  };

  const handleMessage = (event: MessageEvent) => {
    // Only react to our own self-posted UP messages from this origin.
    if (event.origin !== window.location.origin) {
      return;
    }

    const viewport = viewportMessageSchema.safeParse(event.data);
    if (viewport.success) {
      currentViewport = {
        top: viewport.data.top,
        bottom: viewport.data.bottom,
        left: viewport.data.left,
        right: viewport.data.right,
        zoom: viewport.data.zoom,
      };
      // Reply immediately for the new viewport.
      pushCurrentViewport();
      return;
    }

    const detailRequest = sessionDetailRequestMessageSchema.safeParse(
      event.data
    );
    if (detailRequest.success) {
      postAsMockParent(
        buildSessionDetailMessage(
          detailRequest.data.id,
          detailRequest.data.level
        )
      );
    }
  };

  window.addEventListener('message', handleMessage);

  // Live refresh: re-push whatever viewport the SPA last reported.
  const intervalId = window.setInterval(pushCurrentViewport, MOCK_TICK_MS);

  console.debug(
    '[charge-sessions mock] DEV mock parent active — replying to monta:viewport and re-pushing every',
    MOCK_TICK_MS,
    'ms to',
    window.location.origin
  );

  return () => {
    window.clearInterval(intervalId);
    window.removeEventListener('message', handleMessage);
  };
}

/**
 * DEV hook: runs the mock parent for the lifetime of the component when the
 * DEV + `mock=1` + `mapType=chargeSessions` gate is satisfied. No-op otherwise.
 * Mount this next to `useChargeSessionMessages()` in `AppInitializer`.
 *
 * The effect body is fronted by a literal `import.meta.env.DEV` check so that in
 * a production build Vite statically replaces it with `false` and the whole
 * branch — including the only reference to `startChargeSessionMock` and the mock
 * fixtures — is dead-code-eliminated. Result: zero mock code/data in prod.
 */
export function useChargeSessionMockData(): void {
  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }
    if (!isMockChargeSessionsActive()) {
      return;
    }
    return startChargeSessionMock();
  }, []);
}
